import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createProgramPolicy, fetchProgramPolicies, updateProgramPolicyValue } from "@/services/programPolicies";
import type {
    CreateProgramPolicyInput,
    CreateProgramPolicyResponse,
    ProgramPoliciesResponse,
    UpdateProgramPolicyValueInput,
    UpdateProgramPolicyValueResponse
} from "@/types/programPolicies";

import { useClientContextKey } from "./useClientContextKey";

export const PROGRAM_POLICIES_QUERY_KEY = ["program-policies"] as const;

const EMPTY_PROGRAM_POLICIES: ProgramPoliciesResponse = {
    programs: [],
    policies: [],
    snapshotAt: ""
};

interface UseProgramPoliciesReturn {
    data: ProgramPoliciesResponse;
    isLoading: boolean;
    error: Error | null;
}

export function useProgramPolicies(): UseProgramPoliciesReturn {
    const contextKey = useClientContextKey();
    const { data, isLoading, error } = useQuery<ProgramPoliciesResponse, Error>({
        queryKey: [...PROGRAM_POLICIES_QUERY_KEY, contextKey],
        queryFn: fetchProgramPolicies,
        staleTime: 2 * 60 * 1000,
        retry: false
    });

    return {
        data: data ?? EMPTY_PROGRAM_POLICIES,
        isLoading,
        error: error ?? null
    };
}

export function useCreateProgramPolicy(): UseMutationResult<
    CreateProgramPolicyResponse,
    Error,
    CreateProgramPolicyInput
> {
    const queryClient = useQueryClient();
    return useMutation<CreateProgramPolicyResponse, Error, CreateProgramPolicyInput>({
        mutationFn: createProgramPolicy,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: PROGRAM_POLICIES_QUERY_KEY
            });
        }
    });
}

export function useUpdateProgramPolicyValue(): UseMutationResult<
    UpdateProgramPolicyValueResponse,
    Error,
    UpdateProgramPolicyValueInput
> {
    const queryClient = useQueryClient();
    return useMutation<UpdateProgramPolicyValueResponse, Error, UpdateProgramPolicyValueInput>({
        mutationFn: updateProgramPolicyValue,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: PROGRAM_POLICIES_QUERY_KEY
            });
        }
    });
}
